import { useTranslation } from 'react-i18next';
import { useLiveQuery } from 'dexie-react-hooks';
import { MIN_WEIGH_INS_PER_WEEK, weightTrend } from '@kraftwerk/shared';
import { db } from '../../db/local.js';
import { listBodyWeights } from '../../db/journal.js';
import { formatDay } from '../../format.js';

/**
 * Gewicht über die Zeit: Wochenschnitt gegen den der Vorwoche — nicht die
 * Tageszahl, die um ein, zwei Kilo schwankt. Zu wenige Werte: Das steht da,
 * statt eines Trends, den es nicht gibt.
 */
export function WeightTrendTile({ today }: { today: string }): React.JSX.Element | null {
  const { t, i18n } = useTranslation();
  const points = useLiveQuery(() => listBodyWeights(db), [], null);
  if (!points || points.length === 0) return null;

  const trend = weightTrend(points, today);
  const kg = (value: number) => value.toLocaleString(i18n.language, { maximumFractionDigits: 1 });

  return (
    <section aria-labelledby="weight-heading" className="kw-tile flex flex-col gap-1">
      <h3 id="weight-heading" className="kw-kicker">
        {t('journal.weightTrend')}
      </h3>
      {trend.avg7 !== null ? (
        <p>
          <span className="kw-figure text-3xl">{kg(trend.avg7)}</span>{' '}
          <span className="text-sm text-ink-muted">{t('journal.weightAvg')}</span>
        </p>
      ) : null}
      {trend.change !== null ? (
        <p className="text-sm">
          {t('journal.weightChange', {
            change: `${trend.change > 0 ? '+' : trend.change < 0 ? '−' : '±'}${kg(Math.abs(trend.change))}`,
          })}
        </p>
      ) : (
        <p className="text-sm text-ink-muted">
          {t('journal.weightTooFew', { min: MIN_WEIGH_INS_PER_WEEK })}
        </p>
      )}
      {trend.latest ? (
        <p className="text-xs text-ink-muted">
          {t('journal.weightLatest', {
            kg: kg(trend.latest.kg),
            date: formatDay(trend.latest.date, 'date', i18n.language),
          })}
        </p>
      ) : null}
    </section>
  );
}
